import { createBrowserRouter } from 'react-router-dom';

import GoogleCallbackPage from '../pages/Auth/GoogleCallback';
import OAuthAuthorize from '../pages/Auth/OAuthAuthorize';
import InvitedUserOnboarding from '../pages/Auth/InvitedUserOnboarding';
import Login from '../pages/Auth/Login';
import SelectOrganization from '../pages/Auth/SelectOrganization';
import SignUp from '../pages/Auth/SignUp';
import VerifyEmailLogin from '../pages/Auth/VerifyEmailLogin';
import VerifyEmailRegistration from '../pages/Auth/VerifyEmailRegistration';
import VerifyUserInvitation from '../pages/Auth/VerifyUserInvitation';
import BaseAuthLayout from '../pages/AuthorizedWrapper/BaseAuthLayout';
import BasePublicLayout from '../pages/AuthorizedWrapper/BasePublicLayout';
import BaseIndex from '../pages/BaseIndex/BaseIndex';
import WorkspaceOnboarding from '../pages/Onboarding/WorkspaceOnboarding';
import Playground from '../pages/Playground/Playground';
import MyProfile from '../pages/Settings/MyProfile/MyProfile';
import OrganizationSettings from '../pages/Settings/Organization/OrganizationSettings';
import SettingsWrapper from '../pages/Settings/SettingsWrapper';
import IntegrationsSettings from '../pages/Settings/Integrations/IntegrationsSettings';
import McpSettings from '../pages/Settings/Mcp/McpSettings';
import NotificationsSettings from '../pages/Settings/Notifications/NotificationsSettings';
import { NewUserManager } from '../pages/Settings/UserManagement/NewUserManager/NewUserManager';
import NewSpace from '../pages/Space/NewSpace';
import JoinSpace from '../pages/Onboarding/JoinSpace';
import NewSpaceInvite from '../pages/Space/NewSpaceInvite';
import Space from '../pages/Space/Space';
import SpaceWrapper from '../pages/Space/SpaceWrapper';
import NewOrgInvite from '../pages/Onboarding/NewOrgInvite';
import GithubSetupComplete from '../pages/GithubSetupComplete/GithubSetupComplete';
import SlackSetupComplete from '../pages/SlackSetupComplete/SlackSetupComplete';
import { lazy, Suspense } from 'react';
import RoutePaths from './RoutePaths';

const Decisions = lazy(() => import('../pages/Inbox/Inbox'));
const AllTasks = lazy(() => import('../pages/AllTasks/AllTasks'));

const isInboxEnabled = import.meta.env.VITE_FEATURE_INBOX === 'true';
const isAllTasksEnabled = import.meta.env.VITE_FEATURE_ALL_TASKS === 'true';

const createRouter = () =>
  createBrowserRouter([
    /**
     * Authorized wrapper - private routes requiring user to be logged in.
     */
    {
      path: RoutePaths.Home,
      element: <BaseAuthLayout />,
      children: [
        // Any components that want to use space-related navigation and layout but want to be
        // under / route can go in here as a child
        {
          path: '/',
          element: <BaseIndex />,
          children: [
            // {
            //   path: 'dashboard',
            //   element: <Dashboard />,
            // },
            {
              path: ':orgId',
              children: [
                ...(isInboxEnabled
                  ? [
                      {
                        path: 'decisions',
                        element: (
                          <Suspense fallback={null}>
                            <Decisions />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'inbox',
                        element: (
                          <Suspense fallback={null}>
                            <Decisions />
                          </Suspense>
                        ),
                      },
                    ]
                  : []),
                ...(isAllTasksEnabled
                  ? [
                      {
                        path: 'all-tasks',
                        element: (
                          <Suspense fallback={null}>
                            <AllTasks />
                          </Suspense>
                        ),
                      },
                    ]
                  : []),
                {
                  path: 'space',
                  element: <SpaceWrapper />,
                  children: [
                    {
                      path: '/:orgId/space/:spaceId/*',
                      element: <Space />,
                    },
                    {
                      path: '/:orgId/space/:spaceId/task/:taskId',
                      element: <Space />,
                    },
                  ],
                },
              ],
            },
          ],
        },

        {
          path: '*', // Catch-all for any unmatched routes
          element: <BaseIndex />,
        },

        // Onboarding flow
        {
          path: RoutePaths.Onboarding.route,
          children: [
            {
              element: <WorkspaceOnboarding />,
              path: RoutePaths.Onboarding.children.FirstWorkspaceOnboarding,
            },
            {
              element: <InvitedUserOnboarding />,
              path: RoutePaths.Onboarding.children.InvitedUserOnboarding,
            },
          ],
        },

        // Organization invitation onboarding (outside BaseIndex)
        {
          path: '/:orgId/new-org-invite',
          element: <NewOrgInvite />,
        },

        // New space onboarding (outside BaseIndex)
        {
          path: '/:orgId/new-space',
          element: <NewSpace />,
        },

        // Join public space onboarding (outside BaseIndex)
        {
          path: '/:orgId/join-space',
          element: <JoinSpace />,
        },

        // New space invitation (under space routes)
        {
          path: '/:orgId/space/:spaceId/new-space-invite',
          element: <NewSpaceInvite />,
        },

        // To support multi orgs
        {
          path: RoutePaths.LoginSelectOrganization,
          element: <SelectOrganization />,
        },

        {
          path: RoutePaths.GithubSetupComplete,
          element: <GithubSetupComplete />,
        },

        {
          path: RoutePaths.SlackSetupComplete,
          element: <SlackSetupComplete />,
        },

        // Settings routes
        {
          path: RoutePaths.Settings.route,
          element: <SettingsWrapper />,
          children: [
            {
              path: RoutePaths.Settings.children.Personal,
              element: <MyProfile />,
            },
            {
              path: RoutePaths.Settings.children.Organization,
              element: <OrganizationSettings />,
            },
            {
              path: RoutePaths.Settings.children.UserManagement,
              element: <NewUserManager />,
            },
            {
              path: RoutePaths.Settings.children.NewUserManagement,
              element: <NewUserManager />,
            },
            {
              path: RoutePaths.Settings.children.SpaceOwnership,
              element: null,
            },
            {
              path: RoutePaths.Settings.children.Integrations,
              element: <IntegrationsSettings />,
            },
            {
              path: RoutePaths.Settings.children.Notifications,
              element: <NotificationsSettings />,
            },
            {
              path: RoutePaths.Settings.children.Mcp,
              element: <McpSettings />,
            },
          ],
        },

        // Dashboard
        // {
        //   path: RoutePaths.Dashboard,
        //   element: <Dashboard/>,
        // },

        // Playground route used for testing random stuff
        {
          path: RoutePaths.Playground,
          element: <Playground />,
        },
      ],
    },
    /**
     * End of Auth container
     */

    /**
     * PUBLIC ROUTES
     */
    {
      path: RoutePaths.Auth,
      element: <BasePublicLayout />,
      children: [
        {
          path: '/signup',
          element: <SignUp />,
        },
        {
          path: '/login',
          element: <Login />,
        },
      ],
    },
    {
      path: '/auth/verify',
      element: <VerifyEmailRegistration />,
    },
    {
      path: '/signup/verify',
      element: <VerifyEmailRegistration />,
    },
    {
      path: '/verify-email-login',
      element: <VerifyEmailLogin />,
    },
    {
      path: '/verify-user-invitation',
      element: <VerifyUserInvitation />,
    },
    {
      path: '/auth/callback/google',
      element: <GoogleCallbackPage />,
    },
    {
      path: RoutePaths.OAuthAuthorize,
      element: <OAuthAuthorize />,
    },
  ]);

const router = createRouter();
export default router;
